import { ReactElement, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { duplicateMenuSet } from 'src/api/duplicateMenuSet';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { useMenuSets } from 'src/hooks/useMenuSets';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';
import { MENU_SET_DETAILS_LABELS } from '../MenuDetailsFields';
import { DuplicateNameTrayContent, ICloseDuplicateTrayOptions } from '../DuplicateNameTray';

const EXISTING_NAMES_STALE_TIME = 5 * 60 * 1000;

export interface DuplicateMenuSetTrayContentProps {
  menuSetId: string;
  sourceName?: string;
  onClose: (options?: ICloseDuplicateTrayOptions) => void;
  openId: number;
}

export const DuplicateMenuSetTrayContent = ({
  menuSetId,
  sourceName,
  onClose,
  openId,
}: DuplicateMenuSetTrayContentProps): ReactElement => {
  const queryClient = useQueryClient();
  const { trackEvent } = useUserTracking();
  const { data: menuSetsData } = useMenuSets(50, undefined, undefined, undefined, {
    staleTime: EXISTING_NAMES_STALE_TIME,
  });
  const existingMenuSetNames = useMemo(
    () => menuSetsData?.pages.flatMap((page) => page.data.map((menuSet) => menuSet.name)) ?? [],
    [menuSetsData],
  );

  const handleDuplicate = async (name: string) => {
    await duplicateMenuSet(menuSetId, { name });
    await queryClient.invalidateQueries({ queryKey: [QueryKeys.menuSets] });
    trackEvent('menu_set_duplicated', { menuSetId });
  };

  return (
    <DuplicateNameTrayContent
      entityLabel="menu set"
      nameLabel={MENU_SET_DETAILS_LABELS.name}
      sourceName={sourceName}
      defaultName={sourceName && `${sourceName} (Copy)`}
      existingNames={existingMenuSetNames}
      errorTag="menu_set_duplicate"
      openId={openId}
      onDuplicate={handleDuplicate}
      onClose={onClose}
    />
  );
};

export default DuplicateMenuSetTrayContent;
